import { Component, HostBinding, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  defer,
  of,
  Subject,
} from 'rxjs';
import { ResizerService } from 'ZalamoLibrary/src/lib/core/image/resizer.service';
import { ZalamoLocalClientService } from 'ZalamoLibrary/src/lib/ui/zalamo-local-client/zalamo-local-client.service';
import { AsyncClientTaskType } from '../../../../../../ZalamoLocalClient/src/client-tasks/types';
import { LocalPreviewCache } from 'ZalamoLibrary/src/lib/ui/zalamo-local-client/local-preview-cache';
import { FilesFromLocalClientPicker } from 'ZalamoLibrary/src/lib/ui/zalamo-local-client/types';
import {
  catchError,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { CustomLabel } from '../../../graphql';

@Component({
    selector: 'app-icc',
    templateUrl: './icc.component.html',
    styleUrls: ['./icc.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IccComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly previewCache = new LocalPreviewCache(
    (path, size) => this.zalamoLocalClientService.previewUrl(path, size),
    (url) => this.zalamoLocalClientService.releasePreviewUrl(url),
  );
  constructor(
    private resizerService: ResizerService,
    private zalamoLocalClientService: ZalamoLocalClientService,
  ) {}

  filesToBrowse$ = new BehaviorSubject<
    {
      height: number;
      name: string;
      path: string;
      size: number;
      width: number;
      picked: boolean;
      recommended: boolean;
      comment: CustomLabel[];
    }[]
  >([]);
  currentIndex$ = new BehaviorSubject(0);
  pickedImagesCount$ = this.filesToBrowse$.pipe(
    map((images) => images.filter(({ picked }) => picked).length),
  );
  currentImageUrl$ = combineLatest([
    this.filesToBrowse$,
    this.currentIndex$,
  ]).pipe(
    filter(([files, currentIndex]) => files.length > 0),
    switchMap(([filesToBrowse, index]) =>
      defer(() => this.previewCache.load(filesToBrowse[index].path, 3000)).pipe(
        catchError(() => of(null)),
      ),
    ),
  );

  currentImage = combineLatest([this.filesToBrowse$, this.currentIndex$]).pipe(
    filter(([files, currentIndex]) => files.length > 0),
    map(([filesToBrowse, index]) => filesToBrowse[index]),
  );
  cached = new Set();

  async pickFiles() {
    const files =
      await this.zalamoLocalClientService.performTask<FilesFromLocalClientPicker>(
        {
          details: {
            title: 'Wybierz zdjęcia',
            skipSize: false,
          },
          name: AsyncClientTaskType.GetFiles,
        },
      );
    console.log(files);
    this.filesToBrowse$.next(
      files.map((el) => ({
        ...el,
        picked: true,
        recommended: false,
        comment: [],
      })),
    );
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }

  @HostBinding('class.isBlack') isBlack = false;

  ngOnInit(): void {
    this.zalamoLocalClientService.isConnected
      .pipe(
        tap((connected) => {
          if (!connected) {
            this.previewCache.clear();
            this.cached.clear();
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
    combineLatest([this.currentIndex$, this.filesToBrowse$])
      .pipe(
        tap(([currentIndex, images]) => {
          [...images]
            .slice(
              currentIndex + 1,
              currentIndex + 1 + Math.max(5, navigator.hardwareConcurrency),
            )
            .filter(({ path }) => !this.cached.has(path))
            .forEach(({ path }) => {
              this.cached.add(path);
              void this.previewCache
                .load(path, 3000)
                .catch(() => this.cached.delete(path));
            });
        }),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
    fromEvent(document.body, 'keydown')
      .pipe(
        tap((event: KeyboardEvent) => {
          console.log(event);
          switch (event.code) {
            case 'KeyB':
              this.isBlack = !this.isBlack;
              break;
            case 'ArrowRight':
              this.currentIndex$.next(this.currentIndex$.value + 1);
              break;
            case 'ArrowLeft':
              this.currentIndex$.next(this.currentIndex$.value - 1);
              break;
            case 'ArrowDown':
              {
                const temp = [...this.filesToBrowse$.value];
                temp[this.currentIndex$.value].picked = false;
                this.filesToBrowse$.next(temp);
              }
              break;
            case 'ArrowUp':
              {
                const temp = [...this.filesToBrowse$.value];
                temp[this.currentIndex$.value].picked = true;
                this.filesToBrowse$.next(temp);
              }
              break;
            case 'Space':
              {
                const temp = [...this.filesToBrowse$.value];
                temp[this.currentIndex$.value].recommended =
                  !temp[this.currentIndex$.value].recommended;
                this.filesToBrowse$.next(temp);
              }
              break;
          }
        }),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(undefined);
    this.destroyed$.complete();
    this.previewCache.dispose();
  }

  handleFile({ target }: { target: HTMLInputElement }) {
    console.log(target.files);
    const original = new Image();
    original.src = URL.createObjectURL(target.files[0]);
    document.getElementById('original').appendChild(original);
    const fr = new FileReader();
    let seg: Array<number>;

    fr.onload = () => {
      ResizerService.resize(
        target.files[0],
        { width: 2833, height: 4250 },
        { width: 1333, height: 2000 },
      ).then(({ data }) => {
        const resized = new Image();
        resized.src = URL.createObjectURL(data);
        document.getElementById('resized').appendChild(resized);
      });
    };

    fr.readAsArrayBuffer(target.files[0]);
  }
}

const JPEG_ID_SIZE = 2;
const APP_ID_OFFSET = 4;
const APP_MARKER_SIZE = 2;
// From start of APP0 marker.
// From start of APP1 marker.
// From start of APP13 marker.
// From start of APP1 marker.
// From start of APP1 marker including GUID, total length, and offset.
const APP2_ICC_DATA_OFFSET = 18; // From start of APP2 marker including marker and chunk/chunk total numbers.
const APP2_ICC_IDENTIFIER = 'ICC_PROFILE\0';
const ICC_CHUNK_NUMBER_OFFSET = APP_ID_OFFSET + APP2_ICC_IDENTIFIER.length;
const ICC_TOTAL_CHUNKS_OFFSET = ICC_CHUNK_NUMBER_OFFSET + 1;
const SOF0_MARKER = 0xffc0;
const SOF2_MARKER = 0xffc2;
const DHT_MARKER = 0xffc4;
const DQT_MARKER = 0xffdb;
const DRI_MARKER = 0xffdd;
const SOS_MARKER = 0xffda;

const APP0_MARKER = 0xffe0;
const APP2_MARKER = 0xffe2;
const APP15_MARKER = 0xffef;
const COMMENT_MARKER = 0xfffe;
const Constants = {
  USE_FILE: true,
  USE_JFIF: false,
  USE_PNG_FILE: false,
  USE_EXIF: false,
  USE_IPTC: false,
  USE_XMP: false,
  USE_ICC: true,
  USE_MPF: false,
  USE_THUMBNAIL: false,
  USE_TIFF: false,
  USE_JPEG: true,
  USE_PNG: false,
  USE_HEIC: false,
  USE_WEBP: false,
};

function findJpegOffsets(dataView) {
  let appMarkerPosition = JPEG_ID_SIZE;
  let fieldLength;
  let sof0DataOffset;
  let sof2DataOffset;
  let iccChunks;

  while (appMarkerPosition + APP_ID_OFFSET + 5 <= dataView.byteLength) {
    if (Constants.USE_FILE && isSOF0Marker(dataView, appMarkerPosition)) {
      sof0DataOffset = appMarkerPosition + APP_MARKER_SIZE;
    } else if (
      Constants.USE_FILE &&
      isSOF2Marker(dataView, appMarkerPosition)
    ) {
      sof2DataOffset = appMarkerPosition + APP_MARKER_SIZE;
    } else if (
      Constants.USE_ICC &&
      isApp2ICCMarker(dataView, appMarkerPosition)
    ) {
      fieldLength = dataView.getUint16(appMarkerPosition + APP_MARKER_SIZE);
      const iccDataOffset = appMarkerPosition + APP2_ICC_DATA_OFFSET;
      const iccDataLength =
        fieldLength - (APP2_ICC_DATA_OFFSET - APP_MARKER_SIZE);

      const iccChunkNumber = dataView.getUint8(
        appMarkerPosition + ICC_CHUNK_NUMBER_OFFSET,
      );
      const iccChunksTotal = dataView.getUint8(
        appMarkerPosition + ICC_TOTAL_CHUNKS_OFFSET,
      );
      if (!iccChunks) {
        iccChunks = [];
      }
      iccChunks.push({
        offset: iccDataOffset,
        length: iccDataLength,
        chunkNumber: iccChunkNumber,
        chunksTotal: iccChunksTotal,
      });
    } else if (isAppMarker(dataView, appMarkerPosition)) {
      fieldLength = dataView.getUint16(appMarkerPosition + APP_MARKER_SIZE);
    } else {
      break;
    }
    appMarkerPosition += APP_MARKER_SIZE + fieldLength;
  }

  return {
    hasAppMarkers: appMarkerPosition > JPEG_ID_SIZE,
    fileDataOffset: sof0DataOffset || sof2DataOffset,
    iccChunks,
  };
}

export function getStringFromDataView(dataView, offset, length) {
  const chars = [];
  for (let i = 0; i < length && offset + i < dataView.byteLength; i++) {
    chars.push(dataView.getUint8(offset + i));
  }
  return getStringValueFromArray(chars);
}

export function getStringValueFromArray(charArray) {
  return charArray.map((charCode) => String.fromCharCode(charCode)).join('');
}

function isSOF0Marker(dataView, appMarkerPosition) {
  return dataView.getUint16(appMarkerPosition) === SOF0_MARKER;
}

function isSOF2Marker(dataView, appMarkerPosition) {
  return dataView.getUint16(appMarkerPosition) === SOF2_MARKER;
}

function isApp2ICCMarker(dataView, appMarkerPosition) {
  const markerIdLength = APP2_ICC_IDENTIFIER.length;

  return (
    dataView.getUint16(appMarkerPosition) === APP2_MARKER &&
    getStringFromDataView(
      dataView,
      appMarkerPosition + APP_ID_OFFSET,
      markerIdLength,
    ) === APP2_ICC_IDENTIFIER
  );
}

function isAppMarker(dataView, appMarkerPosition) {
  const appMarker = dataView.getUint16(appMarkerPosition);
  return (
    (appMarker >= APP0_MARKER && appMarker <= APP15_MARKER) ||
    appMarker === COMMENT_MARKER ||
    appMarker === SOF0_MARKER ||
    appMarker === SOF2_MARKER ||
    appMarker === DHT_MARKER ||
    appMarker === DQT_MARKER ||
    appMarker === DRI_MARKER ||
    appMarker === SOS_MARKER
  );
}
